import React from 'react';

/**
 * Avatar — a small circular badge that shows either a person's uploaded
 * profile picture OR, as a fallback, their initials on the brand
 * gradient.
 *
 * The product brief:
 *   "show the initials (First letter of First Name & Last Name) besides
 *    each row to who task is assigned or show their image if they have
 *    uploaded picture of themselves in the system."
 *
 * So each row that references a user renders one of these.  The backend
 * (see ``TaskSerializer.get_assignees`` in
 * ``task_management/api_serializers.py``) already computes both the
 * ``initials`` and an absolute ``image_url`` for every assignee, so this
 * component is purely presentational.
 *
 * Props
 * -----
 *   * ``initials`` — 1-2 uppercase letters to show when there's no image.
 *   * ``imageUrl`` — absolute URL of the uploaded picture (optional).
 *   * ``name``     — full name, used for the tooltip / alt text.
 *   * ``size``     — diameter in px (default 28).
 *   * ``title``    — override tooltip (defaults to ``name``).
 */
export default function Avatar({
  initials = '?',
  imageUrl = '',
  name = '',
  size = 28,
  title,
  style = {},
}) {
  const tip = title || name || '';
  const base = {
    width: size,
    height: size,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
    flex: '0 0 auto',
    color: '#fff',
    fontSize: Math.max(10, Math.round(size * 0.4)),
    fontWeight: 700,
    lineHeight: 1,
    background: 'linear-gradient(135deg, #7980ff 0%, #656cfd 100%)',
    boxShadow: '0 2px 6px rgba(121, 128, 255, 0.25)',
    userSelect: 'none',
    ...style,
  };

  if (imageUrl) {
    return (
      <div title={tip} aria-label={tip} style={base}>
        {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
        <img
          src={imageUrl}
          alt={tip || 'Profile picture'}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
    );
  }

  return (
    <div title={tip} aria-label={tip} style={base}>
      {(initials || '?').toString().slice(0, 2).toUpperCase()}
    </div>
  );
}

/**
 * AvatarGroup — render a horizontal, slightly-overlapping stack of
 * avatars for a list of assignees, capping the visible count and
 * showing a "+N" bubble for the overflow.
 *
 * ``people`` is the array of ``{ id, full_name, initials, image_url }``
 * objects the ``TaskSerializer`` returns under ``assignees``.
 */
export function AvatarGroup({ people = [], max = 3, size = 28 }) {
  if (!people || people.length === 0) return null;
  const shown = people.slice(0, max);
  const extra = people.length - shown.length;

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
      {shown.map((p, i) => (
        <div
          key={p.id ?? i}
          style={{ marginLeft: i === 0 ? 0 : -8, border: '2px solid var(--ty-surface, #fff)', borderRadius: '50%' }}
        >
          <Avatar
            initials={p.initials}
            imageUrl={p.image_url}
            name={p.full_name}
            size={size}
          />
        </div>
      ))}
      {extra > 0 && (
        <div
          title={people.slice(max).map((p) => p.full_name).join(', ')}
          style={{
            marginLeft: -8,
            width: size,
            height: size,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            fontSize: Math.max(10, Math.round(size * 0.36)),
            fontWeight: 700,
            color: 'var(--ty-muted, #64748b)',
            background: 'var(--ty-surface-2, #eef0f6)',
            border: '2px solid var(--ty-surface, #fff)',
            flex: '0 0 auto',
          }}
        >
          +{extra}
        </div>
      )}
    </div>
  );
}
